import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postData, ImageFileUpload } from "../../../utility/Utility";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";

export default function UpdateCompanySubCategory() {
  const navigate = useNavigate();
  const location = useLocation();
  console.log({ location });
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [imagePreview, setImagePreview] = useState("");
  const [subCategoryValue, setSubCategoryValue] = useState("");

  // Form state
  const [formData, setFormData] = useState({
    vehicleName: "",
    description: "",
    image: null,
    category: "",
    subCategory: "",
    isActive: true,
  });

  const fetchSubCategories = useCallback(async () => {
    try {
      setLoading(true);
      const response = await postData("api/subcategory/list", {
        category: formData.category,
      });

      if (response?.status) {
        setSubCategories(response?.data || []);
      } else {
        toast.error(response?.message || "Failed to fetch sub categories");
      }
    } catch (error) {
      toast.error("Error fetching sub categories");
      console.error("Fetch sub categories error:", error);
    } finally {
      setLoading(false);
    }
  }, [formData.category]);

  // Set default values from location state
  useEffect(() => {
    if (location.state?.vehicleData) {
      const { vehicleData } = location.state;
      console.log("Vehicle Data:", vehicleData);

      setFormData({
        vehicleName: vehicleData.vehicleName || "",
        description: vehicleData.description || "",
        image: null,
        category:
          vehicleData.category?._id ||
          vehicleData.subCategory?.category?._id ||
          "",
        subCategory: vehicleData.subCategory?._id || "",
        isActive: vehicleData.isActive || true,
      });

      setSubCategoryValue(vehicleData.subCategory?._id || "");
      setImagePreview(vehicleData.image || "");

      // If category is available, fetch its subcategories immediately
      if (vehicleData.category?._id || vehicleData.subCategory?.category?._id) {
        const fetch = async () => {
          try {
            setLoading(true);
            const response = await postData("api/subcategory/list", {
              category:
                vehicleData.category?._id ||
                vehicleData.subCategory?.category?._id,
            });
            if (response?.status) {
              setSubCategories(response?.data || []);
            }
          } catch (error) {
            console.error("Error fetching subcategories:", error);
          } finally {
            setLoading(false);
          }
        };
        fetch();
      }
    }
  }, [location.state]);

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await postData("api/categories/list", {
        page: 1,
        limit: 10000,
      });
      if (response?.status) {
        setCategories(response?.data);
      } else {
        throw new Error(response.data?.message || "Failed to fetch categories");
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Error loading categories"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (formData.category) {
      fetchSubCategories();
    } else {
      setSubCategories([]);
    }
  }, [formData.category, fetchSubCategories]);

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "image" && files && files[0]) {
      const file = files[0];
      setFormData((prev) => ({ ...prev, [name]: file }));

      // Image preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else if (name === "category") {
      setFormData((prev) => ({ ...prev, [name]: value }));
      setSubCategoryValue("");
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Upload image to API
  const uploadImage = async () => {
    if (!formData.image) return null;
    const formDataObj = new FormData();
    formDataObj.append("file", formData.image);

    try {
      const response = await ImageFileUpload(
        "/api/admin/upload?is_upload=s3",
        formDataObj
      );
      const data = await response;
      if (response?.success) {
        return data.url || data.imageUrl || data.filePath;
      } else {
        toast.error(data.message || "Image upload failed");
        return null;
      }
    } catch (error) {
      toast.error("Network error during image upload");
      console.error("Image upload error:", error);
      return null;
    }
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Check category selected
      if (!formData.category) {
        throw new Error("Please select a category");
      }

      // Upload image if selected
      let uploadedImagePath = "";
      if (formData.image) {
        const uploadedPath = await uploadImage();
        if (!uploadedPath) {
          throw new Error("Image upload failed");
        }
        uploadedImagePath = uploadedPath;
      }

      // Prepare payload for both APIs
      const vehiclePayload = {
        vehicleName: formData.vehicleName,
        description: formData.description,
        image: uploadedImagePath || location.state.vehicleData.image,
        category: formData.category,
        subCategory: subCategoryValue,
        isActive: formData.isActive,
        vehicleId: location.state.vehicleData._id,
      };

      const response = await postData("/api/vehicle/update", vehiclePayload);

      if (response?.success || response?.status) {
        toast.success(response.message || "Item added successfully!");
        navigate("/manufacturers-company-subcategory-list");
      } else {
        throw new Error(response?.error || "Failed to add item");
      }
    } catch (error) {
      toast.error(error.message || "Error adding item");
      console.error("Add item error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={5000} />
      <CustomLoader loading={loading} />
      <div className="content">
        <div className="container-fluid">
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <h4 className="fs-18 fw-semibold m-0">Update Vehicle</h4>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              Back
            </button>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">Vehicle Details</h5>
                </div>
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    {/* Category Dropdown */}
                    <div className="row g-3 align-items-center mb-3">
                      <div className="col-md-3">
                        <label htmlFor="category" className="form-label">
                          Category*
                        </label>
                      </div>
                      <div className="col-md-6">
                        <select
                          id="category"
                          name="category"
                          className="form-control"
                          value={formData.category}
                          onChange={handleInputChange}
                          required
                          disabled={loading || categories.length === 0}
                        >
                          <option value="">Select a category</option>
                          {categories.map((category) => (
                            <option key={category._id} value={category._id}>
                              {category.title}
                            </option>
                          ))}
                        </select>
                        {loading && categories.length === 0 && (
                          <small className="text-muted">
                            Loading categories...
                          </small>
                        )}
                        {!loading && categories.length === 0 && (
                          <small className="text-danger">
                            No categories available
                          </small>
                        )}
                      </div>
                    </div>

                    {/* Subcategory Dropdown */}
                    {formData.category && (
                      <div className="row g-3 align-items-center mb-3">
                        <div className="col-md-3">
                          <label
                            htmlFor="subCategoryValue"
                            className="form-label"
                          >
                            Subcategory
                          </label>
                        </div>
                        <div className="col-md-6">
                          <select
                            id="subCategoryValue"
                            name="subCategoryValue"
                            className="form-control"
                            value={subCategoryValue}
                            onChange={(e) =>
                              setSubCategoryValue(e.target.value)
                            }
                            required
                          >
                            <option value="">Select a subcategory</option>
                            {subCategories.map((subCat) => (
                              <option key={subCat._id} value={subCat._id}>
                                {subCat.title}
                              </option>
                            ))}
                          </select>
                          {subCategories.length === 0 && (
                            <small className="text-muted">
                              No subcategories available for this category
                            </small>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Vehicle Name */}
                    <div className="row g-3 align-items-center mb-3">
                      <div className="col-md-3">
                        <label htmlFor="vehicleName" className="form-label">
                          Vehicle Name*
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="text"
                          id="vehicleName"
                          name="vehicleName"
                          className="form-control"
                          placeholder="Enter vehicle name"
                          value={formData.vehicleName}
                          onChange={handleInputChange}
                          required
                          minLength={3}
                          maxLength={100}
                        />
                      </div>
                    </div>

                    {/* Image Upload */}
                    <div className="row g-3 align-items-center mb-3">
                      <div className="col-md-3">
                        <label htmlFor="image" className="form-label">
                          Vehicle Image
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="file"
                          id="image"
                          name="image"
                          className="form-control"
                          onChange={handleInputChange}
                          accept="image/*"
                        />
                        <small className="text-muted">
                          Recommended size: 800x600px, Max size: 2MB
                        </small>
                        {imagePreview && (
                          <div className="mt-2">
                            <img
                              src={imagePreview}
                              alt="Vehicle Preview"
                              className="img-thumbnail"
                              style={{ maxWidth: "200px" }}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Status */}
                    <div className="row g-3 align-items-center mb-3">
                      <div className="col-md-3">
                        <label className="form-label">Status</label>
                      </div>
                      <div className="col-md-6">
                        <div className="form-check form-switch">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="isActive"
                            checked={formData.isActive}
                            onChange={() =>
                              setFormData((prev) => ({
                                ...prev,
                                isActive: !prev.isActive,
                              }))
                            }
                            role="switch"
                          />
                          <label
                            className="form-check-label"
                            htmlFor="isActive"
                          >
                            {formData.isActive ? (
                              <span className="text-success">Active</span>
                            ) : (
                              <span className="text-secondary">Inactive</span>
                            )}
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <div className="row g-3 align-items-center mb-3">
                      <div className="col-md-3">
                        <label htmlFor="description" className="form-label">
                          Description*
                        </label>
                      </div>
                      <div className="col-md-9">
                        <textarea
                          className="form-control"
                          id="description"
                          name="description"
                          rows={5}
                          placeholder="Enter vehicle description"
                          value={formData.description}
                          onChange={handleInputChange}
                          required
                          minLength={10}
                        />
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="d-flex justify-content-end mt-3">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={loading || categories.length === 0}
                      >
                        {loading ? (
                          <CustomLoader loading={true} />
                        ) : (
                          "Update Vehicle"
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
